import React, { useState, useEffect } from 'react';
import Slider from "react-slick";
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import axiosClient from 'api/axiosClient';
import { SETTING_IMAGE_URL } from 'utils/constants';
import './SlideConferences.css';

const SlideItem = ({ item }) => {
    const intl = useIntl();
    const history = useHistory();
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });
    const [status, setStatus] = useState('upcoming'); // 'upcoming' | 'past'

    useEffect(() => {
        // Construct the target date
        const { day, month, year, time_start } = item;
        const targetDateString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${time_start}`;
        const targetDate = new Date(targetDateString).getTime();

        const updateCountdown = () => {
            const now = new Date().getTime();
            const difference = targetDate - now;

            if (difference > 0) {
                setStatus('upcoming');
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
                    seconds: Math.floor((difference % (1000 * 60)) / 1000),
                });
            } else {
                setStatus('past');
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
            }
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [item]);

    // Handle image link
    const imageUrl = item.image ? `${SETTING_IMAGE_URL}/${item.image}` : require('assets/img/custom/slide/1.jpg').default;

    return (
        <div
            className="SlideConferences-item"
            onClick={() => history.push(`/conference-workshop/${item.id}`)}
            style={{ cursor: 'pointer' }}
        >
            <div className="SlideConferences-img-wrapper">
                {status === 'upcoming' && <div className="SlideConferences-upcoming">{intl.formatMessage({ id: 'Upcoming', defaultMessage: 'Upcoming' })}</div>}
                {status === 'past' && <div className="SlideConferences-past">{intl.formatMessage({ id: 'PastEvent', defaultMessage: 'Past Event' })}</div>}
                <img src={imageUrl} alt={item.name} className="SlideConferences-img" />
            </div>
            <div className="SlideConferences-countdown">
                <div className="SlideConferences-time-block">
                    <div className="SlideConferences-time-value">{timeLeft.days}</div>
                    <div className="SlideConferences-time-label">{intl.formatMessage({ id: 'Day' })}</div>
                </div>
                <div className="SlideConferences-time-block">
                    <div className="SlideConferences-time-value">{timeLeft.hours}</div>
                    <div className="SlideConferences-time-label">{intl.formatMessage({ id: 'Hours' })}</div>
                </div>
                <div className="SlideConferences-time-block">
                    <div className="SlideConferences-time-value">{timeLeft.minutes}</div>
                    <div className="SlideConferences-time-label">{intl.formatMessage({ id: 'Minute' })}</div>
                </div>
                <div className="SlideConferences-time-block">
                    <div className="SlideConferences-time-value">{timeLeft.seconds}</div>
                    <div className="SlideConferences-time-label">{intl.formatMessage({ id: 'Second' })}</div>
                </div>
            </div>
        </div>
    );
}

const SlideConferences = () => {
    const [conferencesData, setConferencesData] = useState([]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axiosClient.get(`/events?unit=Home&hightlight=1`);
                if (response.data && response.data.data) {
                    const now = new Date().getTime();
                    const sortedData = [...response.data.data].sort((a, b) => {
                        const getTargetDate = (eventItem) => {
                            const { day, month, year, time_start } = eventItem;
                            return new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${time_start}`).getTime();
                        };
                        const dateA = getTargetDate(a);
                        const dateB = getTargetDate(b);
                        const isUpcomingA = dateA > now;
                        const isUpcomingB = dateB > now;

                        if (isUpcomingA && !isUpcomingB) return -1; // Upcoming first
                        if (!isUpcomingA && isUpcomingB) return 1;
                        if (isUpcomingA) return dateA - dateB; // Soonest upcoming first
                        return dateB - dateA; // Most recent past first
                    });
                    setConferencesData(sortedData);
                }
            } catch (error) {
                console.error("Error fetching events data:", error);
            }
        };

        fetchEvents();
    }, []);

    const settings = {
        infinite: conferencesData.length > 3,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        cssEase: "linear",
        adaptiveHeight: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: Math.min(2, conferencesData.length),
                    slidesToScroll: 1,
                    infinite: conferencesData.length > 2,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    infinite: conferencesData.length > 1,
                }
            }
        ]
    };

    if (!conferencesData || conferencesData.length === 0) {
        return null; // Or some loading state if needed
    }

    return (
        <div className="SlideConferences">
            <Slider {...settings}>
                {conferencesData.map((item, index) => (
                    <SlideItem key={index} item={item} />
                ))}
            </Slider>
        </div>
    );
};

export default SlideConferences;
